import Button from "common/Buttons/Button";
import Tabs from "common/Tabs/Tabs";
import NewLookIntroTabs from "common/Tabs/NewLookIntroTabs";
import React, { useContext, useEffect, useState } from "react";
import Users from "features/admin/group/users/Users";
import Invitations from "features/admin/group/invitations/Invitations.jsx";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { useParams } from "react-router";
import {
  GROUP_MANAGEMENT_TABS_TO_DISPLAY,
  GROUP_MANAGEMENT_TABS,
  MOBILE_SEARCH,
} from "common/constants";
import InvitationModal from "features/admin/group/InvitationModal/InvitationModal";
import Configuration from "features/admin/group/Configuration/Configuration";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { getGroupManagementRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import Flex from "common/Boxes/Flex";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import BuyLicensesModal from "features/admin/group/BuyLicensesModal";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { sharedLib } from "@shared/services/shared.service";
import styled, { useTheme } from "styled-components";
import { Plus, Ticket } from "lucide-react";
import { usePageIntro } from "common/NewLook";
import {
  NEW_LOOK_SECTION_GAP,
  phoneMaxWidth,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout.ts";
import PageTabs from "common/Tabs/PageTabs.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const GroupManagement = () => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { historyPush } = useHistoryWithQuery();
  const { tab } = useParams<{ tab: string }>() || "";
  const activeTab =
    tab?.toUpperCase() in GROUP_MANAGEMENT_TABS
      ? GROUP_MANAGEMENT_TABS[
          tab.toUpperCase() as keyof typeof GROUP_MANAGEMENT_TABS
        ]
      : GROUP_MANAGEMENT_TABS.USERS;
  const [selectedTab, setSelectedTab] = useState(activeTab);
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [buyLicensesModalOpen, setBuyLicensesModalOpen] = useState(false);
  sharedLib.sharedApi.admin.useGetAdminSubGroupsQuery();
  const [subGroupId] = useSelector(
    groupConfigurationsFilters.selectors.selectGroupsFilter
  );
  let { isLoading: getAdminSubGroupsLoading } =
    sharedLib.sharedApi.admin.useGetAdminSubGroupsQuery();
  const { data: adminSubGroupData, isFetching } =
    sharedLib.sharedApi.admin.useGetAdminSubGroupDataQuery(
      subGroupId
        ? {
            sub_group_id: subGroupId,
          }
        : skipToken
    );
  const { analytics } = useContext(AnalyticsContext);

  useEffect(() => {
    if (selectedTab === GROUP_MANAGEMENT_TABS.INVITATIONS) {
      analytics?.adminPanelGroupManagementInvitationsView();
    } else if (selectedTab === GROUP_MANAGEMENT_TABS.USERS) {
      analytics?.adminPanelGroupManagementUsersView();
    } else if (selectedTab === GROUP_MANAGEMENT_TABS.CONFIGURATION) {
      analytics?.adminPanelGroupManagementConfigurationsView();
    }
  }, [analytics, selectedTab]);

  useEffect(() => {
    setSelectedTab(activeTab);
  }, [activeTab]);

  // New look (phones): one primary action, the other quiet; both easy to tap.
  const newLookButtons = (
    <NewLookMobileActions>
      <Button onClick={() => setBuyLicensesModalOpen(true)}>
        <Ticket size={16} strokeWidth={1.75} />
        {t("groupManagement.buyLicenses")}
      </Button>
      <Button type="primary" onClick={() => setInvitationModalOpen(true)}>
        <Plus size={16} strokeWidth={2} />
        {t("groupManagement.inviteUsers")}
      </Button>
    </NewLookMobileActions>
  );

  const buttons = theme.isNewLook ? (
    newLookButtons
  ) : (
    <Flex
      $justifyContent="flex-end"
      style={{
        marginBottom: isBottomNavigator ? 8 : 0,
        width: isMobile ? "100%" : "auto",
        columnGap: 8,
      }}
    >
      <StyledButton
        type="primary"
        onClick={() => setBuyLicensesModalOpen(true)}
      >
        {t("groupManagement.buyLicenses")}
      </StyledButton>
      <StyledButton type="primary" onClick={() => setInvitationModalOpen(true)}>
        <PlusOutlined />
        {t("groupManagement.inviteUsers")}
      </StyledButton>
    </Flex>
  );

  const tabToComponent = {
    [GROUP_MANAGEMENT_TABS.USERS]: (
      <Users
        subGroupID={subGroupId}
        isFetching={isFetching}
        mobileActions={buttons}
      />
    ),
    [GROUP_MANAGEMENT_TABS.INVITATIONS]: (
      <Invitations
        subGroupID={subGroupId}
        isFetching={isFetching}
        mobileActions={buttons}
      />
    ),
    [GROUP_MANAGEMENT_TABS.CONFIGURATION]: (
      <Configuration subGroupID={subGroupId} mobileActions={buttons} />
    ),
  };

  const tabsItems = Object.values(GROUP_MANAGEMENT_TABS).map((iterTab) => ({
    key: iterTab,
    label: t(GROUP_MANAGEMENT_TABS_TO_DISPLAY[iterTab]),
  }));

  // New look: the actions and tabs move into the page intro.
  const isNewLookDesktop = theme.isNewLook && !isBottomNavigator;
  usePageIntro(
    isNewLookDesktop
      ? {
          actions: (
            <>
              <Button onClick={() => setBuyLicensesModalOpen(true)}>
                <Ticket size={16} strokeWidth={1.75} />
                {t("groupManagement.buyLicenses")}
              </Button>
              <Button
                type="primary"
                onClick={() => setInvitationModalOpen(true)}
              >
                <Plus size={16} strokeWidth={2} />
                {t("groupManagement.inviteUsers")}
              </Button>
            </>
          ),
          footer: (
            <NewLookIntroTabs
              activeKey={selectedTab}
              onTabClick={(tabKey: string) =>
                historyPush(getGroupManagementRoute(tabKey.toLowerCase()))
              }
              items={tabsItems}
            />
          ),
        }
      : {},
    [isNewLookDesktop, selectedTab, i18n.language]
  );

  const itemsWithChildren = tabsItems.map((tab) => {
    return {
      ...tab,
      children: (
        <div style={{ height: "100%", overflowY: "auto" }}>
          {tabToComponent[tab.key]}
        </div>
      ),
      link: getGroupManagementRoute(tab.key.toLowerCase()),
    };
  });

  const content = isBottomNavigator ? (
    <PageTabs items={itemsWithChildren} />
  ) : (
    tabToComponent[selectedTab]
  );

  const modals = (
    <>
      {invitationModalOpen && (
        <InvitationModal
          subGroupID={subGroupId}
          closeModal={() => setInvitationModalOpen(false)}
        />
      )}
      <BuyLicensesModal
        open={buyLicensesModalOpen}
        closeModal={() => setBuyLicensesModalOpen(false)}
        adminSubGroupData={adminSubGroupData}
      />
    </>
  );

  if (isNewLookDesktop) {
    return (
      <LoadingSpinnerOverlay
        isLoading={getAdminSubGroupsLoading}
        hideContentWhileLoading={true}
        spinnerSize="large"
        spinnerMarginTop={100}
        backgroundColor={theme.colors.layoutBgColor}
      >
        <NewLookContent>
          {modals}
          {tabToComponent[selectedTab]}
        </NewLookContent>
      </LoadingSpinnerOverlay>
    );
  }

  return (
    <LoadingSpinnerOverlay
      isLoading={getAdminSubGroupsLoading}
      hideContentWhileLoading={true}
      spinnerSize="large"
      spinnerMarginTop={100}
      backgroundColor={theme.colors.layoutBgColor}
    >
      <div style={{ padding: isBottomNavigator ? 0 : 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          {!isBottomNavigator && (
            <Flex style={{ width: "100%", flexWrap: "wrap" }}>
              <Tabs
                activeKey={selectedTab}
                onTabClick={(tabKey: string) =>
                  historyPush(getGroupManagementRoute(tabKey.toLowerCase()))
                }
                items={tabsItems}
              />
              {buttons}
            </Flex>
          )}
          {invitationModalOpen && (
            <InvitationModal
              subGroupID={subGroupId}
              closeModal={() => setInvitationModalOpen(false)}
            />
          )}
          <BuyLicensesModal
            open={buyLicensesModalOpen}
            closeModal={() => setBuyLicensesModalOpen(false)}
            adminSubGroupData={adminSubGroupData}
          />
        </div>
        {content}
      </div>
    </LoadingSpinnerOverlay>
  );
};

export default GroupManagement;

const NewLookContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_SECTION_GAP}px;
  padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} ${NEW_LOOK_PAGE_GUTTER_VAR};
`;
const NewLookMobileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  .ant-btn {
    flex: 1 1 0;
    min-width: fit-content;
    height: 40px;
  }
`;
const StyledButton = styled(Button)`
  @media (max-width: ${phoneMaxWidth}) {
    font-size: ${(p) => p.theme.typography.fontSizes.S};
    height: 28px;
    padding: 0 12px;
  }
`;
